
import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove, update } from 'firebase/database';
import { db } from '../../firebase';
import { Announcement } from '../../types';
import { Plus, Trash2, X, Check, Megaphone, Calendar, Clock, Image as ImageIcon, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';

const AdminAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    buttonText: '',
    buttonLink: '',
    type: 'popup' as 'popup' | 'banner',
    isActive: true,
    expiryDate: ''
  });

  useEffect(() => {
    const annRef = ref(db, 'announcements');
    onValue(annRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAnnouncements(Object.values(data));
      } else {
        setAnnouncements([]);
      }
    });
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newRef = push(ref(db, 'announcements'));
    const finalData: Announcement = {
      ...formData,
      id: newRef.key as string,
      createdAt: Date.now(),
      expiryDate: formData.expiryDate ? new Date(formData.expiryDate).getTime() : undefined
    };

    await set(newRef, finalData);
    setIsModalOpen(false);
    setFormData({ title: '', description: '', image: '', buttonText: '', buttonLink: '', type: 'popup', isActive: true, expiryDate: '' });
  };

  const toggleActive = async (id: string, current: boolean) => {
    await update(ref(db, `announcements/${id}`), { isActive: !current });
  };

  const deleteAnnouncement = async (id: string) => {
    if (window.confirm('Bu duyuruyu silmek istediğinize emin misiniz?')) {
      await remove(ref(db, `announcements/${id}`));
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Kampanya & Duyuru Yönetimi</h1>
          <p className="text-stone-500 font-medium italic mt-1">Giriş popup'ları ve kampanya banner'larını buradan yönetin.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-amber-800 text-white px-8 py-4 rounded-2xl font-bold flex items-center space-x-3 shadow-xl shadow-amber-900/20 hover:bg-amber-900 transition-all"
        >
          <Plus size={20} />
          <span>Yeni Kampanya Oluştur</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {announcements.map((ann) => (
          <div key={ann.id} className={`bg-white rounded-[2.5rem] border ${ann.isActive ? 'border-amber-200' : 'border-stone-100'} shadow-sm overflow-hidden flex flex-col group`}>
            {ann.image && (
              <div className="h-48 overflow-hidden relative">
                <img src={ann.image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={ann.title} />
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest text-stone-900">
                  {ann.type}
                </div>
              </div>
            )}
            <div className="p-8 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-stone-900 line-clamp-1">{ann.title}</h3>
                <button onClick={() => toggleActive(ann.id, ann.isActive)}>
                  {ann.isActive ? <ToggleRight className="text-amber-700" size={32} /> : <ToggleLeft className="text-stone-300" size={32} />}
                </button>
              </div>
              <p className="text-stone-500 text-sm mb-6 line-clamp-3 leading-relaxed">{ann.description}</p>
              
              <div className="space-y-3 mb-8 text-xs font-bold text-stone-400 mt-auto">
                <div className="flex items-center space-x-2">
                  <Calendar size={14} />
                  <span>Bitiş: {ann.expiryDate ? new Date(ann.expiryDate).toLocaleDateString('tr-TR') : 'Süresiz'}</span>
                </div>
                {ann.buttonLink && (
                  <div className="flex items-center space-x-2">
                    <ExternalLink size={14} />
                    <span className="truncate">{ann.buttonLink}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2 pt-6 border-t border-stone-50">
                <button 
                  onClick={() => deleteAnnouncement(ann.id)}
                  className="flex-1 py-3 bg-red-50 text-red-500 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-100 transition-colors"
                >
                  SİL
                </button>
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div className="col-span-full py-20 bg-stone-50 rounded-[3rem] border-2 border-dashed border-stone-200 flex flex-col items-center justify-center text-stone-400">
            <Megaphone size={48} className="mb-4 opacity-20" />
            <p className="font-medium italic">Henüz aktif bir duyuru veya kampanya bulunmuyor.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-8 border-b border-stone-50 flex items-center justify-between">
              <h2 className="text-2xl font-black text-stone-900">Kampanya Detayları</h2>
              <button onClick={() => setIsModalOpen(false)}><X size={28} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto scrollbar-hide">
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Başlık</label>
                  <input 
                    type="text" required
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                    placeholder="Örn: Yılbaşı İndirimi Başladı!"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Açıklama</label>
                  <textarea 
                    required rows={3}
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 resize-none"
                    placeholder="Kampanya detaylarını buraya yazın..."
                  ></textarea>
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Buton Metni</label>
                  <input 
                    type="text"
                    value={formData.buttonText}
                    onChange={e => setFormData({...formData, buttonText: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                    placeholder="Alışverişe Başla"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Buton Linki</label>
                  <input 
                    type="text"
                    value={formData.buttonLink}
                    onChange={e => setFormData({...formData, buttonLink: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                    placeholder="/category/mumlar"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Kampanya Türü</label>
                  <select 
                    value={formData.type}
                    onChange={e => setFormData({...formData, type: e.target.value as 'popup' | 'banner'})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                  >
                    <option value="popup">Giriş Popup'ı</option>
                    <option value="banner">Üst Duyuru Bandı</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Bitiş Tarihi (Opsiyonel)</label>
                  <input 
                    type="datetime-local"
                    value={formData.expiryDate}
                    onChange={e => setFormData({...formData, expiryDate: e.target.value})}
                    className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Kampanya Görseli</label>
                  <label className="w-full aspect-video border-2 border-dashed border-stone-200 rounded-[2rem] flex flex-col items-center justify-center cursor-pointer hover:bg-stone-50 transition-colors overflow-hidden">
                    {formData.image ? (
                      <img src={formData.image} className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <ImageIcon size={32} className="text-stone-300 mb-2" />
                        <span className="text-xs font-bold text-stone-400">Görsel Seçmek İçin Tıklayın</span>
                      </>
                    )}
                    <input type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                  </label>
                </div>
              </div>
              <button 
                type="submit"
                className="w-full bg-amber-800 text-white py-5 rounded-2xl font-bold text-lg hover:bg-amber-900 shadow-xl shadow-amber-900/20 transition-all active:scale-95"
              >
                Kaydet ve Yayına Al
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
